import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ImagePreviewModalProps {
  src: string;
  alt?: string;
  onClose: () => void;
}

const ZOOM_LEVELS = [1, 1.5, 2, 3];

export default function ImagePreviewModal({
  src,
  alt,
  onClose,
}: ImagePreviewModalProps) {
  const { t } = useTranslation("common");
  const [zoomIndex, setZoomIndex] = useState(0);
  const zoom = ZOOM_LEVELS[zoomIndex];
  const isZoomed = zoomIndex > 0;
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // When zoom changes, recenter the scroll so the user sees the middle of the
  // image instead of being parked at the top-left corner.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
      el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
    });
  }, [zoomIndex]);

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomIndex((prev) => (prev + 1) % ZOOM_LEVELS.length);
  };

  const resetZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    setZoomIndex(0);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[10000] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative w-[90vw] h-[90vh] max-w-6xl flex flex-col rounded-lg bg-black/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button — placed INSIDE the container with safe spacing so it
            is never clipped, regardless of the modal's overflow settings. */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-red-500 hover:bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-colors z-20"
          aria-label={t("avisosModal.closePreview")}
        >
          <i className="ri-close-line text-2xl leading-none"></i>
        </button>

        <div
          ref={scrollerRef}
          className={`flex-1 rounded-lg ${
            isZoomed ? "overflow-auto" : "overflow-hidden"
          }`}
        >
          <div
            className="w-full h-full flex items-center justify-center p-2 sm:p-4"
            style={
              isZoomed
                ? {
                    width: `${100 * zoom}%`,
                    height: `${100 * zoom}%`,
                    minWidth: "100%",
                    minHeight: "100%",
                  }
                : undefined
            }
          >
            <img
              src={src}
              alt={alt || t("avisosModal.previewAlt")}
              onClick={handleImageClick}
              className={`max-w-full max-h-full object-contain select-none transition-all duration-200 ${
                zoomIndex === ZOOM_LEVELS.length - 1
                  ? "cursor-zoom-out"
                  : "cursor-zoom-in"
              }`}
              draggable={false}
            />
          </div>
        </div>

        {isZoomed && (
          <button
            type="button"
            onClick={resetZoom}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 hover:bg-black/90 text-white px-3 py-1 rounded-full text-xs flex items-center gap-1 transition-colors z-10"
            aria-label={t("avisosModal.resetZoom")}
          >
            <i className="ri-refresh-line"></i>
            <span>{Math.round(zoom * 100)}%</span>
          </button>
        )}
      </div>
    </div>
  );
}
